import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectLogInUsername } from '../../../common/authenticateDashboard/authSlice';
import { useNavigate } from "react-router-dom";
import { TiArrowBack } from 'react-icons/ti';

// Function to get product test analytics
async function getProductTestAnalytics(clientName, testId, startDate, endDate) {
    try {
        const response = await fetch(`https://causalfunnel-21-wordpress-plugin.firebaseio.com/${clientName}/tests/${testId}.json`);
        const testData = await response.json();
        
        if (!testData) {
            return { error: "No test data found", summary: null, testData: null };
        }
        
        // Calculate metrics
        const summary = {
            testName: testData.name,
            status: testData.status,
            type: testData.type,
            created: testData.created,
            control: {
                views: testData.views?.a || 0,
                conversions: testData.conversions?.a || 0,
                addToCart: testData.add_to_cart?.a || 0,
                purchases: testData.purchases?.a || 0,
                revenue: testData.revenue?.a || 0,
                conversionRate: 0,
                purchaseRate: 0,
                addToCartRate: 0,
                avgOrderValue: 0
            },
            variant: {
                views: testData.views?.b || 0,
                conversions: testData.conversions?.b || 0,
                addToCart: testData.add_to_cart?.b || 0,
                purchases: testData.purchases?.b || 0,
                revenue: testData.revenue?.b || 0,
                conversionRate: 0,
                purchaseRate: 0,
                addToCartRate: 0,
                avgOrderValue: 0
            }
        };
        
        // Calculate rates for control
        if (summary.control.views > 0) {
            summary.control.conversionRate = parseFloat((summary.control.conversions / summary.control.views * 100).toFixed(2));
            summary.control.purchaseRate = parseFloat((summary.control.purchases / summary.control.views * 100).toFixed(2));
            summary.control.addToCartRate = parseFloat((summary.control.addToCart / summary.control.views * 100).toFixed(2));
        }
        if (summary.control.purchases > 0) {
            summary.control.avgOrderValue = parseFloat((summary.control.revenue / summary.control.purchases).toFixed(2));
        }
        
        // Calculate rates for variant
        if (summary.variant.views > 0) {
            summary.variant.conversionRate = parseFloat((summary.variant.conversions / summary.variant.views * 100).toFixed(2));
            summary.variant.purchaseRate = parseFloat((summary.variant.purchases / summary.variant.views * 100).toFixed(2));
            summary.variant.addToCartRate = parseFloat((summary.variant.addToCart / summary.variant.views * 100).toFixed(2));
        }
        if (summary.variant.purchases > 0) {
            summary.variant.avgOrderValue = parseFloat((summary.variant.revenue / summary.variant.purchases).toFixed(2));
        }
        
        // Calculate totals
        const totalViews = summary.control.views + summary.variant.views;
        const totalConversions = summary.control.conversions + summary.variant.conversions;
        const totalRevenue = summary.control.revenue + summary.variant.revenue;
        const totalPurchases = summary.control.purchases + summary.variant.purchases;
        
        summary.overall = {
            views: totalViews,
            conversions: totalConversions,
            purchases: totalPurchases,
            revenue: totalRevenue,
            conversionRate: totalViews > 0 ? parseFloat((totalConversions / totalViews * 100).toFixed(2)) : 0,
            purchaseRate: totalViews > 0 ? parseFloat((totalPurchases / totalViews * 100).toFixed(2)) : 0,
            avgOrderValue: totalPurchases > 0 ? parseFloat((totalRevenue / totalPurchases).toFixed(2)) : 0
        };
        
        return {
            summary,
            testData
        };
        
    } catch (error) {
        console.error('Error fetching product test data:', error);
        return { 
            error: error.message, 
            summary: null, 
            testData: null
        };
    }
}

// Function to get list of available product tests
async function getAvailableProductTests(clientName) {
    try {
        const response = await fetch(`https://causalfunnel-21-wordpress-plugin.firebaseio.com/${clientName}/tests.json?shallow=true`);
        const data = await response.json();
        return Object.keys(data || {});
    } catch (error) {
        console.error('Error fetching product tests:', error);
        return [];
    }
}

const ProductABTest = () => {
    const sessionUser = useSelector(selectLogInUsername);
    const user = sessionUser?.split("@")[0];
    const clientName = user?.split("-prod")[0];
    const navigate = useNavigate();

    // State for analytics data
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // State for test selection
    const [availableTests, setAvailableTests] = useState([]);
    const [selectedTest, setSelectedTest] = useState('');
    const [loadingTests, setLoadingTests] = useState(true);

    // Load available tests and automatically select first test
    useEffect(() => {
        if (clientName) {
            const fetchTests = async () => {
                setLoadingTests(true);
                try {
                    const tests = await getAvailableProductTests(clientName);
                    setAvailableTests(tests);
                    
                    // Auto-select first test and fetch its data
                    if (tests.length > 0) {
                        setSelectedTest(tests[0]);
                        const result = await getProductTestAnalytics(clientName, tests[0], null, null);
                        if (result.error) {
                            setError(result.error);
                        } else {
                            setAnalyticsData(result);
                        }
                    }
                } catch (err) {
                    setError('Failed to load product tests');
                } finally {
                    setLoadingTests(false);
                }
            };
            fetchTests();
        }
    }, [clientName]);

    // Fetch analytics data
    const fetchAnalytics = async (testId = selectedTest) => {
        if (!testId) {
            setError('Please select a test');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const result = await getProductTestAnalytics(clientName, testId, null, null);
            if (result.error) {
                setError(result.error);
            } else {
                setAnalyticsData(result);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Handle test selection change
    const handleTestChange = (testId) => {
        setSelectedTest(testId);
        fetchAnalytics(testId);
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount);
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString();
        } catch {
            return dateString;
        }
    };

    // Determine winner based on conversion rate
    const getWinner = () => {
        if (!analyticsData?.summary) return null;
        const { control, variant } = analyticsData.summary;
        if (variant.conversionRate > control.conversionRate) return 'B';
        if (control.conversionRate > variant.conversionRate) return 'A';
        return 'Tie';
    };

    return (
        <div className="flex-grow p-6" >
            {/* Back Button */}
            <div className="mb-6">
                <button
                    className="bg-indigo-500 text-white px-4 py-2 rounded-lg border 
                    border-indigo-500 hover:bg-indigo-600 transition-all duration-300 
                    ease-in-out font-semibold text-lg flex items-center gap-2"
                    onClick={() => navigate("/goals")}
                >
                    <TiArrowBack className="text-xl"/>
                    Go Back to View Report Selection
                </button>
            </div>

            <div className="bg-white rounded-lg shadow-lg">
                {/* Header with Test Selection */}
                <div className="bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 rounded-t-lg">
                    <div className="px-6 py-6">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                            {/* Left side - Title and description */}
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-3 h-3 bg-purple-600 rounded-full animate-pulse"></div>
                                    <h1 className="text-2xl font-bold">Product A/B Test Analytics</h1>
                                </div>
                                <div className="text-purple-600">
                                    {analyticsData?.summary ? (
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                            <span className="font-medium">{analyticsData.summary.testName}</span>
                                            <span className="hidden sm:inline">•</span>
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                analyticsData.summary.status === 'active' 
                                                    ? 'bg-green-300 text-green-700' 
                                                    : 'bg-gray-300 text-gray-700'
                                            }`}>
                                                {analyticsData.summary.status}
                                            </span>
                                            <span className="hidden sm:inline">•</span>
                                            <span className="bg-purple-300 text-purple-700 px-2 py-1 rounded text-xs font-medium">
                                                {analyticsData.summary.type}
                                            </span>
                                        </div>
                                    ) : (
                                        <span>Product Test Dashboard for {clientName || 'Client'}</span>
                                    )}
                                </div>
                            </div>

                            {/* Right side - Test Selection */}
                            <div className="flex-shrink-0 w-full lg:w-1/3">
                                <div className="bg-white bg-opacity-90 border border-purple-300 rounded-lg p-4 min-w-3xl">
                                    <label className="block text-purple-700 text-sm font-medium mb-2">
                                        Select Product Test:
                                    </label>
                                    <select
                                        value={selectedTest}
                                        onChange={(e) => handleTestChange(e.target.value)}
                                        className="w-full px-3 py-2 bg-white text-purple-900 border border-purple-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        disabled={loadingTests}
                                    >
                                        <option value="">Choose a product test to analyze</option>
                                        {availableTests.map(test => (
                                            <option key={test} value={test}>{test}</option>
                                        ))}
                                    </select>
                                    {loadingTests && (
                                        <div className="text-purple-600 text-xs mt-1">Loading tests...</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Test Creation Date */}
                        {analyticsData?.summary?.created && (
                            <div className="mt-4 pt-4 border-t border-purple-300">
                                <div className="flex items-center gap-2 text-purple-600">
                                    <span className="text-sm">Created:</span>
                                    <span className="text-sm font-medium">
                                        {formatDate(analyticsData.summary.created)}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {loadingTests && (
                        <div className="text-center py-8">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                            <p className="mt-2 text-gray-600">Loading available product tests...</p>
                        </div>
                    )}

                    {loading && (
                        <div className="text-center py-8">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                            <p className="mt-2 text-gray-600">Loading product test data...</p>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                            <div className="text-red-800 font-medium">Error loading data:</div>
                            <div className="text-red-700 text-sm mt-1">{error}</div>
                        </div>
                    )}

                    {!clientName && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
                            <div className="text-yellow-800 font-medium">Authentication Required</div>
                            <div className="text-yellow-700 text-sm mt-1">Please log in to access your product test data.</div>
                        </div>
                    )}

                    {analyticsData && !loading && !error && (
                        <>
                            {/* Summary Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                {/* Control Group */}
                                <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-semibold text-blue-800">Control (A)</h3>
                                        {analyticsData.testData?.control?.url && (
                                            <a
                                                href={analyticsData.testData.control.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="px-2 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors duration-200 no-underline"
                                            >
                                                Preview
                                            </a>
                                        )}
                                    </div>
                                    <div className="space-y-2 text-sm">
                                        <p><span className="font-medium">Views:</span> {analyticsData.summary.control.views}</p>
                                        <p><span className="font-medium">Add to Cart:</span> {analyticsData.summary.control.addToCart}</p>
                                        <p><span className="font-medium">Purchases:</span> {analyticsData.summary.control.purchases}</p>
                                        <p><span className="font-medium">Revenue:</span> {formatCurrency(analyticsData.summary.control.revenue)}</p>
                                        <div className="pt-2 border-t border-blue-200">
                                            <p><span className="font-medium">Conv. Rate:</span> {analyticsData.summary.control.conversionRate}%</p>
                                            <p><span className="font-medium">Purchase Rate:</span> {analyticsData.summary.control.purchaseRate}%</p>
                                            <p><span className="font-medium">AOV:</span> {formatCurrency(analyticsData.summary.control.avgOrderValue)}</p>
                                        </div>
                                    </div>
                                    {analyticsData.testData?.control?.title && (
                                        <div className="mt-3 pt-3 border-t border-blue-200">
                                            <p className="text-xs text-blue-600 font-medium">{analyticsData.testData.control.title}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Variant Group */}
                                <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-semibold text-green-800">Variant (B)</h3>
                                        {analyticsData.testData?.variant?.url && (
                                            <a
                                                href={analyticsData.testData.variant.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="px-2 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors duration-200 no-underline"
                                            >
                                                Preview
                                            </a>
                                        )}
                                    </div>
                                    <div className="space-y-2 text-sm">
                                        <p><span className="font-medium">Views:</span> {analyticsData.summary.variant.views}</p>
                                        <p><span className="font-medium">Add to Cart:</span> {analyticsData.summary.variant.addToCart}</p>
                                        <p><span className="font-medium">Purchases:</span> {analyticsData.summary.variant.purchases}</p>
                                        <p><span className="font-medium">Revenue:</span> {formatCurrency(analyticsData.summary.variant.revenue)}</p>
                                        <div className="pt-2 border-t border-green-200">
                                            <p><span className="font-medium">Conv. Rate:</span> {analyticsData.summary.variant.conversionRate}%</p>
                                            <p><span className="font-medium">Purchase Rate:</span> {analyticsData.summary.variant.purchaseRate}%</p>
                                            <p><span className="font-medium">AOV:</span> {formatCurrency(analyticsData.summary.variant.avgOrderValue)}</p>
                                        </div>
                                    </div>
                                    {analyticsData.testData?.variant?.title && (
                                        <div className="mt-3 pt-3 border-t border-green-200">
                                            <p className="text-xs text-green-600 font-medium">{analyticsData.testData.variant.title}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Overall Summary */}
                                <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
                                    <h3 className="text-lg font-semibold text-purple-800 mb-4">Test Summary</h3>
                                    <div className="space-y-2 text-sm">
                                        <p><span className="font-medium">Total Views:</span> {analyticsData.summary.overall.views}</p>
                                        <p><span className="font-medium">Total Revenue:</span> {formatCurrency(analyticsData.summary.overall.revenue)}</p>
                                        <p><span className="font-medium">Total Purchases:</span> {analyticsData.summary.overall.purchases}</p>
                                        <div className="pt-2 border-t border-purple-200">
                                            <p><span className="font-medium">Overall Conv. Rate:</span> {analyticsData.summary.overall.conversionRate}%</p>
                                            <p><span className="font-medium">Overall AOV:</span> {formatCurrency(analyticsData.summary.overall.avgOrderValue)}</p>
                                            <p><span className="font-medium">Winner:</span> 
                                                <span className={`ml-1 font-bold ${
                                                    getWinner() === 'B' ? 'text-green-600' : 
                                                    getWinner() === 'A' ? 'text-blue-600' : 'text-gray-600'
                                                }`}>
                                                    {getWinner() === 'Tie' ? 'Tie' : `Group ${getWinner()}`}
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Product Details Comparison Table */}
                            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
                                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                    <h3 className="text-lg font-semibold text-gray-800">Product Details Comparison</h3>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Attribute</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Control (A)</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Variant (B)</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            <tr>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Title</td>
                                                <td className="px-6 py-4 text-sm text-gray-500">{analyticsData.testData?.control?.title || 'N/A'}</td>
                                                <td className="px-6 py-4 text-sm text-gray-500">{analyticsData.testData?.variant?.title || 'N/A'}</td>
                                            </tr>
                                            <tr>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Button Text</td>
                                                <td className="px-6 py-4 text-sm text-gray-500">{analyticsData.testData?.control?.button_text || 'N/A'}</td>
                                                <td className="px-6 py-4 text-sm text-gray-500">{analyticsData.testData?.variant?.button_text || 'N/A'}</td>
                                            </tr>
                                            <tr>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Regular Price</td>
                                                <td className="px-6 py-4 text-sm text-gray-500">
                                                    {analyticsData.testData?.control?.regular_price ? formatCurrency(analyticsData.testData.control.regular_price) : 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-500">
                                                    {analyticsData.testData?.variant?.regular_price ? formatCurrency(analyticsData.testData.variant.regular_price) : 'N/A'}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Sale Price</td>
                                                <td className="px-6 py-4 text-sm text-gray-500">
                                                    {analyticsData.testData?.control?.sale_price ? formatCurrency(analyticsData.testData.control.sale_price) : 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-500">
                                                    {analyticsData.testData?.variant?.sale_price ? formatCurrency(analyticsData.testData.variant.sale_price) : 'N/A'}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">SKU</td>
                                                <td className="px-6 py-4 text-sm text-gray-500">{analyticsData.testData?.control?.sku || 'N/A'}</td>
                                                <td className="px-6 py-4 text-sm text-gray-500">{analyticsData.testData?.variant?.sku || 'N/A'}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Performance Metrics Table */}
                            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                    <h3 className="text-lg font-semibold text-gray-800">Performance Metrics</h3>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Metric</th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Control (A)</th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Variant (B)</th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Difference</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            <tr>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Views</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{analyticsData.summary.control.views}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{analyticsData.summary.variant.views}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                    <span className={`font-medium ${
                                                        analyticsData.summary.variant.views > analyticsData.summary.control.views ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                        {analyticsData.summary.variant.views - analyticsData.summary.control.views > 0 ? '+' : ''}
                                                        {analyticsData.summary.variant.views - analyticsData.summary.control.views}
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Add to Cart</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{analyticsData.summary.control.addToCart}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{analyticsData.summary.variant.addToCart}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                    <span className={`font-medium ${
                                                        analyticsData.summary.variant.addToCart > analyticsData.summary.control.addToCart ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                        {analyticsData.summary.variant.addToCart - analyticsData.summary.control.addToCart > 0 ? '+' : ''}
                                                        {analyticsData.summary.variant.addToCart - analyticsData.summary.control.addToCart}
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Purchases</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{analyticsData.summary.control.purchases}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{analyticsData.summary.variant.purchases}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                    <span className={`font-medium ${
                                                        analyticsData.summary.variant.purchases > analyticsData.summary.control.purchases ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                        {analyticsData.summary.variant.purchases - analyticsData.summary.control.purchases > 0 ? '+' : ''}
                                                        {analyticsData.summary.variant.purchases - analyticsData.summary.control.purchases}
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Revenue</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{formatCurrency(analyticsData.summary.control.revenue)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{formatCurrency(analyticsData.summary.variant.revenue)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                    <span className={`font-medium ${
                                                        analyticsData.summary.variant.revenue > analyticsData.summary.control.revenue ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                        {analyticsData.summary.variant.revenue - analyticsData.summary.control.revenue > 0 ? '+' : ''}
                                                        {formatCurrency(analyticsData.summary.variant.revenue - analyticsData.summary.control.revenue)}
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Conversion Rate</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                    <span className="text-blue-600 font-medium">{analyticsData.summary.control.conversionRate}%</span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                    <span className="text-green-600 font-medium">{analyticsData.summary.variant.conversionRate}%</span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                    <span className={`font-medium ${
                                                        analyticsData.summary.variant.conversionRate > analyticsData.summary.control.conversionRate ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                        {analyticsData.summary.variant.conversionRate - analyticsData.summary.control.conversionRate > 0 ? '+' : ''}
                                                        {(analyticsData.summary.variant.conversionRate - analyticsData.summary.control.conversionRate).toFixed(2)}%
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Purchase Rate</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                    <span className="text-blue-600 font-medium">{analyticsData.summary.control.purchaseRate}%</span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                    <span className="text-green-600 font-medium">{analyticsData.summary.variant.purchaseRate}%</span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                    <span className={`font-medium ${
                                                        analyticsData.summary.variant.purchaseRate > analyticsData.summary.control.purchaseRate ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                        {analyticsData.summary.variant.purchaseRate - analyticsData.summary.control.purchaseRate > 0 ? '+' : ''}
                                                        {(analyticsData.summary.variant.purchaseRate - analyticsData.summary.control.purchaseRate).toFixed(2)}%
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Avg Order Value</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                    <span className="text-blue-600 font-medium">{formatCurrency(analyticsData.summary.control.avgOrderValue)}</span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                    <span className="text-green-600 font-medium">{formatCurrency(analyticsData.summary.variant.avgOrderValue)}</span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                    <span className={`font-medium ${
                                                        analyticsData.summary.variant.avgOrderValue > analyticsData.summary.control.avgOrderValue ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                        {analyticsData.summary.variant.avgOrderValue - analyticsData.summary.control.avgOrderValue > 0 ? '+' : ''}
                                                        {formatCurrency(analyticsData.summary.variant.avgOrderValue - analyticsData.summary.control.avgOrderValue)}
                                                    </span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Product Images Section */}
                            {(analyticsData.testData?.control?.image_url || analyticsData.testData?.variant?.image_url) && (
                                <div className="mt-6 bg-white border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                        <h3 className="text-lg font-semibold text-gray-800">Product Images</h3>
                                    </div>
                                    <div className="p-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {/* Control Image */}
                                            <div className="text-center">
                                                <h4 className="text-md font-semibold text-blue-800 mb-3">Control (A)</h4>
                                                {analyticsData.testData.control?.image_url ? (
                                                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                                        <img 
                                                            src={analyticsData.testData.control.image_url} 
                                                            alt="Control product"
                                                            className="max-w-full h-48 object-contain mx-auto rounded"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.nextSibling.style.display = 'block';
                                                            }}
                                                        />
                                                        <div className="hidden text-gray-500 text-sm mt-2">Image not available</div>
                                                    </div>
                                                ) : (
                                                    <div className="border border-gray-200 rounded-lg p-8 bg-gray-50 text-gray-500">
                                                        No image available
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* Variant Image */}
                                            <div className="text-center">
                                                <h4 className="text-md font-semibold text-green-800 mb-3">Variant (B)</h4>
                                                {analyticsData.testData.variant?.image_url ? (
                                                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                                        <img 
                                                            src={analyticsData.testData.variant.image_url} 
                                                            alt="Variant product"
                                                            className="max-w-full h-48 object-contain mx-auto rounded"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.nextSibling.style.display = 'block';
                                                            }}
                                                        />
                                                        <div className="hidden text-gray-500 text-sm mt-2">Image not available</div>
                                                    </div>
                                                ) : (
                                                    <div className="border border-gray-200 rounded-lg p-8 bg-gray-50 text-gray-500">
                                                        No image available
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Product Descriptions Section */}
                            {(analyticsData.testData?.control?.description || analyticsData.testData?.variant?.description) && (
                                <div className="mt-6 bg-white border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                        <h3 className="text-lg font-semibold text-gray-800">Product Descriptions</h3>
                                    </div>
                                    <div className="p-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {/* Control Description */}
                                            <div>
                                                <h4 className="text-md font-semibold text-blue-800 mb-3">Control (A)</h4>
                                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                    <p className="text-sm text-gray-700">
                                                        {analyticsData.testData.control?.description || 'No description available'}
                                                    </p>
                                                </div>
                                            </div>
                                            
                                            {/* Variant Description */}
                                            <div>
                                                <h4 className="text-md font-semibold text-green-800 mb-3">Variant (B)</h4>
                                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                                    <p className="text-sm text-gray-700">
                                                        {analyticsData.testData.variant?.description || 'No description available'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Test Insights */}
                            <div className="mt-6 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-indigo-800 mb-4">📊 Test Insights</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <h4 className="font-medium text-indigo-700 mb-2">Performance Summary</h4>
                                        <ul className="space-y-1 text-indigo-600">
                                            <li>• {getWinner() === 'B' ? 'Variant (B)' : getWinner() === 'A' ? 'Control (A)' : 'Neither version'} is performing better</li>
                                            <li>• Total test views: {analyticsData.summary.overall.views.toLocaleString()}</li>
                                            <li>• Total revenue generated: {formatCurrency(analyticsData.summary.overall.revenue)}</li>
                                            <li>• Overall conversion rate: {analyticsData.summary.overall.conversionRate}%</li>
                                        </ul>
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-indigo-700 mb-2">Key Metrics</h4>
                                        <ul className="space-y-1 text-indigo-600">
                                            <li>• Revenue difference: {formatCurrency(Math.abs(analyticsData.summary.variant.revenue - analyticsData.summary.control.revenue))}</li>
                                            <li>• Conversion rate difference: {Math.abs(analyticsData.summary.variant.conversionRate - analyticsData.summary.control.conversionRate).toFixed(2)}%</li>
                                            <li>• AOV difference: {formatCurrency(Math.abs(analyticsData.summary.variant.avgOrderValue - analyticsData.summary.control.avgOrderValue))}</li>
                                            <li>• Test status: {analyticsData.summary.status}</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {!analyticsData && !loading && !error && !loadingTests && availableTests.length === 0 && (
                        <div className="text-center py-12">
                            <div className="text-gray-400 text-6xl mb-4">📊</div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Product Tests Found</h3>
                            <p className="text-gray-600">No product tests are available for this client. Start by creating a product test to see analytics here.</p>
                        </div>
                    )}

                    {!analyticsData && !loading && !error && !loadingTests && availableTests.length > 0 && !selectedTest && (
                        <div className="text-center py-12">
                            <div className="text-gray-400 text-6xl mb-4">🎯</div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Product Test</h3>
                            <p className="text-gray-600">Choose a product test from the dropdown above to view its analytics and performance metrics.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductABTest;